/**
 * stalactitePulsePipeline — mineral stalactites growing downward
 * with concentric rings.
 *
 * A row of stalactites hangs from the top of the screen, each
 * growing incrementally as rings of mineral deposit accumulate
 * along its length. Each stalactite has a slightly different growth
 * rate, so the row visibly evolves over time.
 *
 * Innovations:
 *   - Each stalactite is a 2D SDF (a downward-pointing cone tapered
 *     along its length), with a growth-rate function that adds
 *     rings at specific time intervals.
 *   - The rings are detected by sampling the stalactite's height
 *     function and counting how many discrete "lifts" have been
 *     added since t=0 (modulo a per-stalactite phase).
 *   - Mineral color: warm amber-copper with subtle iridescent
 *     shimmer on the rings.
 *   - Drip drops that fall from the stalactite tips at irregular
 *     intervals.
 *   - Growth pulses: when bass hits, the stalactites "grow" faster
 *     (more rings per second).
 *
 * Audio:
 *   - bass: growth rate
 *   - mid:  stalactite thickness
 *   - high: ring brightness
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface StalactitePulsePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — cave dark.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.10, 0.06, 0.04),
    vec3(0.02, 0.01, 0.01),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Rock ceiling at the top — slightly visible bumpy surface.
  float ceiling = -0.30 + 0.02 * sin(p.x * 30.0);
  float ceilingMask = smoothstep(0.005, 0.0, p.y - ceiling);
  vec3 ceilingColor = vec3(0.30, 0.22, 0.18);
  col = mix(col, ceilingColor, ceilingMask * 0.7);

  // 9 stalactites hanging from the ceiling.
  // Each has a different x, growth rate, and length.
  float growthRate = 0.4 + 0.4 * bass;
  const int N = 9;
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    float xCenter = (fi - 4.0) * 0.22;
    // Length — grows over time.
    float L = 0.25 + 0.35 * fract(growthRate * uTime * 0.3 + hash21(vec2(fi, 1.0)));
    // Width at the base (thicker = mid-driven).
    float wBase = 0.045 + 0.025 * mid;
    // Each stalactite is a downward-pointing cone: SDF is
    // |p.x - xCenter| - wBase * (1 - (p.y - ceiling) / L)
    // for p.y in [ceiling, ceiling + L].
    float dy = p.y - ceiling;
    if (dy > 0.0 && dy < L) {
      float w = wBase * (1.0 - dy / L);
      float d = abs(p.x - xCenter) - w;
      float stalactiteMask = 1.0 - smoothstep(0.0, 0.005, d);
      // Rings — count the discrete "lifts" based on time.
      float ringFreq = 40.0 + 30.0 * bass;
      float ringPos = dy * ringFreq;
      float ringMask = abs(fract(ringPos) - 0.5) * 2.0;
      ringMask = smoothstep(0.85, 0.95, ringMask);
      // Base color — amber with variations.
      float hueShift = hash21(vec2(fi, 2.0));
      vec3 stColor = mix(
        vec3(0.85, 0.55, 0.25),
        vec3(0.95, 0.75, 0.45),
        hueShift
      );
      // Rings are brighter.
      stColor = mix(stColor, vec3(1.0, 0.95, 0.7), ringMask * 0.7);
      col = mix(col, stColor, stalactiteMask);
      // Inner shadow.
      float inner = smoothstep(0.0, w, abs(p.x - xCenter));
      col -= inner * 0.05 * stalactiteMask;
    }
  }

  // Drip drops falling from stalactite tips.
  for (int i = 0; i < N; i++) {
    float fi = float(i);
    float xCenter = (fi - 4.0) * 0.22;
    float L = 0.25 + 0.35 * fract(growthRate * uTime * 0.3 + hash21(vec2(fi, 1.0)));
    float tipY = ceiling + L;
    // Drip cycles — each stalactite drips at a different rate.
    float dripPeriod = 1.0 + 0.5 * hash21(vec2(fi, 3.0)) - 0.3 * bass;
    float dripPhase = fract(uTime / dripPeriod + hash21(vec2(fi, 4.0)));
    float dripY = tipY + dripPhase * 0.5;
    if (dripY < 0.6) {
      float dx = p.x - xCenter;
      float dd = length(p - vec2(xCenter, dripY));
      float dripMask = exp(-dd * 200.0);
      col += vec3(1.0, 0.8, 0.5) * dripMask * 0.7;
    }
    // Ripple where the drip hits the bottom.
    float bottomY = -0.5;
    float dropletY = bottomY + 0.05;
    float lifeT = dripPhase * dripPeriod;
    float rippleR = lifeT * 0.3;
    float rdist = abs(length(p - vec2(xCenter, dropletY)) - rippleR);
    float ripple = smoothstep(0.008, 0.0, rdist) * (1.0 - lifeT);
    col += vec3(0.9, 0.7, 0.4) * ripple * 0.5;
  }

  // Mineral crystals scattered on the ground.
  for (int i = 0; i < 12; i++) {
    float fi = float(i);
    vec2 cp = vec2(
      sin(fi * 1.7) * 0.8,
      -0.55 + 0.04 * fract(sin(fi * 3.1) * 43.0)
    );
    float cr = 0.01 + 0.02 * hash21(vec2(fi, 5.0));
    float cd = length(p - cp);
    float crystal = exp(-cd * 200.0);
    col += vec3(0.85, 0.7, 0.5) * crystal * 0.6;
  }

  // Warm ambient light from below — illuminating the stalactites.
  float ambient = exp(-(p.y + 0.3) * 1.5) * 0.3;
  col += vec3(1.0, 0.7, 0.4) * ambient;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeStalactitePulseBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeStalactitePulseRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createStalactitePulsePipeline(gl: WebGL2RenderingContext): StalactitePulsePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawStalactitePulse(
  gl: WebGL2RenderingContext,
  pipe: StalactitePulsePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
